// Client for admin alert email settings. Backend: api/core/routers/admin_alerts.py (platform admins only).

import { ApiError, TokenGetter } from './learningApi';

export interface AlertSettings {
  enabled: boolean;
  recipients: string[];
  subject_prefix: string;
  model_alerts_enabled: boolean;
  digest_enabled: boolean;
  digest_hour_utc: number;
  platform_alerts_enabled: boolean;
  window_minutes: number;
  min_runs: number;
  error_rate_pct: number;
  timeout_rate_pct: number;
  p95_deadline_pct: number;
  p95_ms: number | null;
  cooldown_minutes: number;
  recovery_emails: boolean;
  /** "Normal" for the slowdown and traffic checks = the previous baseline_days, excluding the window. */
  baseline_days: number;
  slowdown_enabled: boolean;
  slowdown_pct: number;
  slowdown_min_ms: number;
  fallback_enabled: boolean;
  fallback_rate_pct: number;
  traffic_drop_enabled: boolean;
  traffic_drop_pct: number;
  updated_by: string | null;
  updated_at: string | null;
}

export type NumericSetting =
  | 'digest_hour_utc'
  | 'window_minutes'
  | 'min_runs'
  | 'error_rate_pct'
  | 'timeout_rate_pct'
  | 'p95_deadline_pct'
  | 'p95_ms'
  | 'cooldown_minutes'
  | 'baseline_days'
  | 'slowdown_pct'
  | 'slowdown_min_ms'
  | 'fallback_rate_pct'
  | 'traffic_drop_pct';

export interface AlertSettingsResponse {
  settings: AlertSettings;
  /** False when the settings row is missing and defaults (or ADMIN_ALERT_EMAILS) apply. */
  saved: boolean;
  environment: string;
  limits: Record<NumericSetting, { min: number; max: number; integer: boolean }> & { max_recipients: number; max_prefix: number };
}

export type AlertKind = 'platform_function' | 'model_down' | 'model_recovered' | 'digest' | 'test';

export interface AlertLogItem {
  id: number;
  kind: AlertKind;
  key: string | null;
  state: 'firing' | 'resolved' | 'sent';
  subject: string | null;
  recipients: string[];
  details: Record<string, unknown> & { emailed?: boolean; failed?: string[] };
  /** null: not emailed (recovery emails off) or still sending. */
  sent_ok: boolean | null;
  created_at: string | null;
}

export interface AlertReason {
  code: 'error_rate' | 'timeouts' | 'near_deadline' | 'p95_deadline' | 'p95_ms' | 'slowdown' | 'fallback' | 'traffic_drop';
  label: string;
  value: number;
  threshold: number;
}

export interface PreviewRow {
  key: string;
  customer_id: string;
  customer_tag: string | null;
  function_name: string;
  function_id: string | null;
  platform_tool: string | null;
  custom: boolean;
  go_timeout_s: number | null;
  runs: number;
  ok: number;
  error_rate: number | null;
  timed_out: number;
  timeout_rate: number | null;
  near_deadline: number | null;
  near_deadline_rate: number | null;
  p95_ms: number | null;
  p95_limit_ms: number | null;
  /** Median of successful runs in the window. */
  p50_ms: number | null;
  /** Median over the previous baseline_days; null when the slowdown check is off or there isn't enough history. */
  normal_p50_ms: number | null;
  normal_samples: number;
  /** Runs answered by a backup model instead of the selected one. */
  fallback: number;
  fallback_rate: number | null;
  /** Average runs in this clock window on previous days; only when the traffic drop check is on. */
  normal_runs: number | null;
  reasons: AlertReason[];
  last_state: 'firing' | 'resolved' | null;
  last_at: string | null;
  action: 'fire' | 'resolve' | null;
  status: 'new' | 'reminder' | 'cooldown' | 'recovered' | 'quiet' | 'waiting' | 'too_few_runs' | 'ok';
  note: string;
  emailed: boolean;
  top_errors: { label: string; runs: number }[];
}

export interface AlertPreview {
  evaluated_at: string;
  window_minutes: number;
  would_email: boolean;
  subject: string | null;
  inactive_reason: string | null;
  rows: PreviewRow[];
}

export interface TestResult {
  subject: string;
  results: { email: string; ok: boolean }[];
  sent_ok: boolean;
}

export type EditableSettings = Omit<AlertSettings, 'updated_by' | 'updated_at'>;

export function adminAlertsApi(getToken: TokenGetter) {
  async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const token = await getToken();
    const res = await fetch(`/api/v2/admin/alerts${path}`, {
      ...init,
      headers: { Authorization: `Bearer ${token}`, ...(init.body ? { 'Content-Type': 'application/json' } : {}) },
    });
    if (!res.ok) {
      let message = res.statusText;
      try {
        const body = await res.json();
        message = typeof body.detail === 'string' ? body.detail : JSON.stringify(body.detail || body);
      } catch {
        /* keep statusText */
      }
      throw new ApiError(message, res.status);
    }
    return res.json();
  }

  return {
    settings: () => request<AlertSettingsResponse>('/settings'),
    save: (settings: EditableSettings) => request<AlertSettingsResponse>('/settings', { method: 'PUT', body: JSON.stringify(settings) }),
    test: (recipients?: string[]) => request<TestResult>('/test', { method: 'POST', body: JSON.stringify(recipients ? { recipients } : {}) }),
    log: (limit = 100, kind?: AlertKind) =>
      request<{ kinds: AlertKind[]; items: AlertLogItem[] }>(`/log?limit=${limit}${kind ? `&kind=${kind}` : ''}`),
    preview: (settings?: EditableSettings) =>
      settings ? request<AlertPreview>('/preview', { method: 'POST', body: JSON.stringify(settings) }) : request<AlertPreview>('/preview'),
  };
}
